import { useEffect, useState } from 'react';
import {
  Button,
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselFooter,
  CarouselIndicator,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@qodo/design-system';
import { Copy, CopyCheck } from 'lucide-react';
import { Step } from 'shared/InstallationDialog';
import { useCopyToClipboard } from 'usehooks-ts';
import { spawn } from 'utils/translations';
import { GIT_INTEGRATION_GITLAB_BASE_URL } from '../../../network/httpClients/GitlabClient/consts';
import { getServerUrl } from '../../../network/httpClients/utils';
import { Image } from '../../Image';
import { getCreateAppGuideCarouselSteps } from '../configuration';
import { GITLAB_CALLBACK_PATH } from '../consts';
import { CarouselStepConfig } from '../types';

export const CreateAppGuide = () => {
  const [copiedValue, copy] = useCopyToClipboard();
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const spawnCreateAppGuide = spawn(
    'dialogs.gitlab-installation.create-app-guide',
  );

  const redirectUri = new URL(
    `${GIT_INTEGRATION_GITLAB_BASE_URL}${GITLAB_CALLBACK_PATH}`,
    getServerUrl(),
  ).toString();

  const isCopied = copiedValue === redirectUri;

  const carouselSteps = getCreateAppGuideCarouselSteps();

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    setCurrentSlideIndex(carouselApi.selectedScrollSnap());

    const onSelect = () => {
      setCurrentSlideIndex(carouselApi.selectedScrollSnap());
    };

    carouselApi.on('select', onSelect);

    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi]);

  const renderCarouselItem = (step: CarouselStepConfig) => {
    const title = spawnCreateAppGuide(`${step.id}.title`);

    return (
      <CarouselItem key={step.id} className="basis-full h-full flex flex-col">
        <Image
          source={step.image}
          alt={title}
          title={title}
          className="min-h-0 flex-1 rounded-md opacity-80 object-contain"
        />
      </CarouselItem>
    );
  };

  const renderCarouselIndicator = (step: CarouselStepConfig, index: number) => (
    <CarouselIndicator
      key={step.id}
      index={index}
      isCurrent={index === currentSlideIndex}
      className="uppercase"
    >
      {index + 1}. {spawnCreateAppGuide(`${step.id}.title`)}
    </CarouselIndicator>
  );

  const renderCarousel = () => (
    <Carousel
      setApi={setCarouselApi}
      className="w-full flex-1 min-h-0 flex flex-col"
    >
      <CarouselContent className="h-full">
        {carouselSteps.map(renderCarouselItem)}
      </CarouselContent>
      <CarouselFooter>
        <CarouselPrevious type="button" />
        <div className="flex items-center justify-center gap-6 font-mono text-sm uppercase">
          {carouselSteps.map(renderCarouselIndicator)}
        </div>
        <CarouselNext type="button" />
      </CarouselFooter>
    </Carousel>
  );

  const renderCopyIcon = () => {
    if (isCopied) {
      return <CopyCheck className="size-3 text-text-action-positive-idle" />;
    }

    return <Copy className="size-3 text-text-general-neutral-mid" />;
  };

  return (
    <Step
      className="h-full"
      title={spawnCreateAppGuide('title')}
      description={spawnCreateAppGuide('description')}
    >
      <Button
        size="xs"
        variant="outline"
        className="group flex gap-1.5 self-start"
        onClick={() => copy(redirectUri)}
      >
        {spawnCreateAppGuide('redirect-uri-label')}
        {renderCopyIcon()}
      </Button>

      <div className="flex flex-col flex-1 min-h-0 gap-4 pt-4 pr-5 pb-2 pl-5 bg-fill-interactive-neutral-idle">
        <div className="text-sm text-text-general-neutral-subtle">
          {spawnCreateAppGuide('carousel-description')}
        </div>
        {renderCarousel()}
      </div>
    </Step>
  );
};
